import React from 'react';
import { Lock } from 'react-feather';
import { useAIProvider } from '../context/AIProviderContext';

/**
 * Toggle that switches the active AI provider between GPT-5.5 and Claude.
 * Reads / writes through AIProviderContext — no local state needed.
 *
 * Pass `locked` once a result has been produced: the control greys out, can no
 * longer be switched, and clearly shows which engine is in use.
 */
export default function AIProviderToggle({ className = '', locked = false }) {
  const { provider, toggle } = useAIProvider();
  const isClaude = provider === 'claude';

  return (
    <div className={`flex items-center gap-3 ${locked ? 'opacity-90' : ''} ${className}`}>
      {/* GPT-5.5 label */}
      <span
        className={`text-sm font-semibold transition-colors duration-200 ${
          !isClaude ? (locked ? 'text-gray-600' : 'text-emerald-600') : 'text-gray-400'
        }`}
      >
        GPT-5.5
      </span>

      {/* Toggle track */}
      <button
        onClick={locked ? undefined : toggle}
        disabled={locked}
        role="switch"
        aria-checked={isClaude}
        aria-disabled={locked}
        title={locked ? `Locked — using ${isClaude ? 'Claude' : 'GPT-5.5'} for this resume` : 'Switch AI engine'}
        className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors duration-300 focus:outline-none ${
          locked
            ? 'bg-gray-300 cursor-not-allowed'
            : `focus:ring-2 focus:ring-offset-2 ${
                isClaude
                  ? 'bg-violet-600 focus:ring-violet-500'
                  : 'bg-emerald-500 focus:ring-emerald-400'
              }`
        }`}
      >
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform duration-300 ${
            isClaude ? 'translate-x-8' : 'translate-x-1'
          }`}
        />
      </button>

      {/* Claude label */}
      <span
        className={`text-sm font-semibold transition-colors duration-200 ${
          isClaude ? (locked ? 'text-gray-600' : 'text-violet-600') : 'text-gray-400'
        }`}
      >
        Claude
      </span>

      {/* Active pill — when locked, shows a lock + "Using …" */}
      <span
        className={`ml-1 inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-bold transition-all duration-300 ${
          locked
            ? 'bg-gray-100 text-gray-600'
            : isClaude
            ? 'bg-violet-100 text-violet-700'
            : 'bg-emerald-100 text-emerald-700'
        }`}
      >
        {locked && <Lock className="w-3 h-3" />}
        {locked ? `Using ${isClaude ? 'Claude' : 'GPT-5.5'}` : isClaude ? 'Anthropic' : 'OpenAI'}
      </span>
    </div>
  );
}
